import { format } from 'date-fns'

interface IGTMParameters {
	[key: string]: any
}

export const useGTMService = () => {
	if (process.server) return
	const config = useNuxtApp().$config.public
	// if (config.environment !== 'production' && config.environment !== 'staging') return
	const userStore = useUserStore()
	const route = useRoute()

	// Użytkownik klika na button “search” na głównej stronie lub na innej stronie (np. listing ofert i ponowne kliknięcie w button)
	const searchButtonClicked = (params: IGTMParameters) => {
		params = { ...params, ...getBookerData(), ...getCurrency() }
		pushDataLayer('search_button_clicked', params)
	}

	// Użytkownik otrzymał listę aut dostępnych w danej lokalizacji
	// przy każdym odświeżeniu, event odpala się na nowo np. zmiana filtrów lub jakiegoś parametru przez Usera
	const searchListingViewed = (params: IGTMParameters) => {
		params = { ...params, ...getBookerData(), ...getCurrency() }
		pushDataLayer('search_listing_viewed', params)
	}

	// 	Użytkownik zmienia dolne filtry na listingu dostępnych samochodów
	// - jeśli użytkownik klika na “usuń wszystkie filtry” to mamy interaction_type “remove” oraz element_value “all”
	// - każda zmiana/dodanie/usunięcie triggeruje nowy event - zmiana kilku filtrów to kilka osobnych eventów
	// - “remove” może nastąpić poprzed odkliknięcie lub “x” na przypiętych chipsach
	const listingFiltersInteraction = (params: IGTMParameters) => {
		pushDataLayer('listing_filters_interaction', params)
	}

	// Użytkownik klika na dany samochód z listingu produktów
	const vehicleClicked = (params: IGTMParameters) => {
		params = { ...params, ...getBookerData(), ...getCurrency() }
		pushDataLayer('vehicle_clicked', params)
	}

	const vehicleViewed = (params: IGTMParameters) => {
		params = { ...params, ...getBookerData(), ...getCurrency() }
		pushDataLayer('vehicle_clicked', params)
	}

	// Użytkownik kliknął przejdź dalej lub wybrał jedno z ubezpieczeń na stronie Product Page
	const bookingStarted = (params: IGTMParameters) => {
		params = { ...params, ...getBookerData(), ...getBookingData(), ...getCurrency() }
		pushDataLayer('booking_started', params)
	}

	const purchase = (params: IGTMParameters) => {
		params = { ...params, ...getCurrency() }
		pushDataLayer('purchase', params)
	}

	const insuranceSelected = (params: IGTMParameters) => {
		params = { ...params, ...getCurrency() }
		pushDataLayer('insurance_selected', params)
	}

	// Użytkownik dodał jeden z upselli w ramach procesu bookingowego.
	const bookingServiceAdded = (params: IGTMParameters) => {
		params = { ...params, ...getCurrency() }
		pushDataLayer('booking_services_added', params)
	}

	// OStatni krok rezerwacji
	const checkoutStarted = (params: IGTMParameters) => {
		params = { ...params, ...getBookerData(), ...getBookingData(), ...getCurrency() }
		pushDataLayer('checkout_started', params)
	}

	// Wybrana metoda płatności
	const addPaymentInfo = (params: IGTMParameters) => {
		pushDataLayer('add_payment_info', params)
	}

	const userLogin = (params: IGTMParameters) => {
		pushDataLayer('user_sign_in', params)
	}

	const userRegister = (params: IGTMParameters) => {
		pushDataLayer('user_sign_up', params)
	}

	const formStarted = (params: IGTMParameters) => {
		pushDataLayer('form_started', params)
	}

	const newsletterSubmit = (params: IGTMParameters) => {
		pushDataLayer('newsletter_submit', params)
	}

	const pageViewed = (params: IGTMParameters) => {
		pushDataLayer('page_viewed', params)
	}

	const offerInterest = (params: IGTMParameters) => {
		pushDataLayer('offer_interest', params)
	}

	const pushDataLayer = (eventName: string, params?: { [key: string]: string | number }) => {
		// @ts-ignore google tag manager Name
		if (!dataLayer) return

		const user = userStore.getUser
		const { $i18n } = useNuxtApp()
		const locale = $i18n.locale

		const userBasic = {
			user_id: user?.id,
			user_email: user?.email,
			user_language: locale.value,
			custom_id: getUserCustomUUID(),
			env: config.environment,
			item_list_id: route.name,
			item_list_url: route.fullPath,
		}
		const dates = {
			event_date: formatDate(new Date()),
		}
		if (params && params.ecommerce) {
			if (params.car_id) {
				// @ts-ignore google tag manager Name
				dataLayer.push({ car_id: null, ecommerce: null })
			}
			// @ts-ignore google tag manager Name
			else dataLayer.push({ ecommerce: null })
		}
		// @ts-ignore google tag manager Name
		dataLayer.push({ event: eventName, ...params, ...dates, ...userBasic })
	}

	const generateFallbackUUID = () => {
		return 'xxxxxxxxxxxx'.replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16))
	}

	const getUserCustomUUID = () => {
		const uuid = useCookie('cf-uuid').value

		if (!uuid) {
			const newUUID = (typeof crypto?.randomUUID === 'function' ? crypto?.randomUUID() : generateFallbackUUID())?.replace(/-/g, '').slice(0, 12)

			if (newUUID) {
				useCookie('cf-uuid').value = newUUID
			}

			return newUUID
		}

		return uuid
	}

	const pushParamsToDataLayer = (params: IGTMParameters) => {
		// @ts-ignore google tag manager Name
		if (!dataLayer) return
		// @ts-ignore google tag manager Name
		dataLayer.push({ ...params })
	}

	const formatDate = (date: Date, dateformat: string = 'yyyy-MM-dd HH:mm') => {
		if (!date) return null
		return format(date, dateformat)
	}

	const getCurrency = () => {
		return { currency: useCookie('currency').value }
	}

	const getBookerData = () => {
		const bookingStore = useBookingStore()
		const bookerState = useBookerStore().getBooker()
		const startLocation = bookerState.selectedLocations.start
		const endLocation = bookerState.selectedLocations.end ? bookerState.selectedLocations.end : bookerState.selectedLocations.start

		return {
			date_in: formatDate(bookerState.selectedDates.start),
			pickup_city: startLocation?.city,
			pickup_point: getLocationPoint(startLocation),
			date_out: formatDate(bookerState.selectedDates.end),
			return_city: endLocation?.city,
			return_point: getLocationPoint(endLocation),
			custom_pickup_address: !!startLocation?.isPickupLocation,
			custom_return_address: !!endLocation?.isPickupLocation,
			custom_pickup_price: startLocation?.isPickupLocation ? bookerState.deliveryCosts?.start : null,
			custom_return_price: endLocation?.isPickupLocation
				? bookerState.deliveryCosts?.end
					? bookerState.deliveryCosts?.end
					: bookerState.deliveryCosts?.start
				: null,
			days: bookingStore.getDays,
		}
	}

	const getBookingData = () => {
		const bookingStore = useBookingStore()
		const booking = bookingStore.getBooking
		if (!booking) return {}
		const exchangeRate = useCookie('currency-exchange-rate')?.value ?? 1
		const car = bookingStore.getSelectedCar
		const addonServices = bookingStore.getAddonServices
		const ecommerce = {
			...getCurrency(),
			value: Number(((booking.total ?? 0) * Number(exchangeRate ?? 1)).toFixed(2)),
			discount: Number(((booking.totalDiscount ?? 0) * Number(exchangeRate ?? 1)).toFixed(2)),
			pending_points: booking.totalPendingPoints,
			rent: Number(((booking.totalRent ?? 0) * Number(exchangeRate ?? 1)).toFixed(2)),
			addition: Number(((booking.totalAddition ?? 0) * Number(exchangeRate ?? 1)).toFixed(2)),
			insurance: Number(((booking.totalInsurance ?? 0) * Number(exchangeRate ?? 1)).toFixed(2)),
			deposit: Number(((booking.deposit ?? 0) * Number(exchangeRate ?? 1)).toFixed(2)),
			coupon: booking.coupon?.code,
		}
		const items = [
			{
				item_id: car ? car.id : null,
				item_brand: car ? car.brand.toLowerCase() : null,
				item_name: getCarNameLabel(car),
				item_category: getCarSegmentLabel(car),
				segment_id: car && car.segment ? car.segment.id : null,
				service_group_id: [
					...new Set(booking.bookingServices?.filter((service) => service.isSelected).map((service) => service.serviceGroupID)),
				],
				quantity: 1,
				price: Number(((booking.total ?? 0) * Number(exchangeRate ?? 1)).toFixed(2)),
				...getCurrency(),
			},
		]
		const addons: any[] = []
		if (addonServices)
			addonServices.forEach((service) => {
				if (!service.isSelected) return
				addons.push({
					service_type_name: service.serviceTypeName,
					service_id: service.serviceID,
					service_group_id: service.serviceGroupID,
					service_name: service.name,
					day_price: Number((((service.amount ?? 0) * Number(exchangeRate ?? 1)) / (bookingStore.getDays ?? 1)).toFixed(2)),
					...getCurrency(),
					quantity: service.quantity,
					discount: Number(((service.discount_total_gross ?? 0) * Number(exchangeRate ?? 1)).toFixed(2)),
				})
			})
		ecommerce.items = items
		return { ecommerce, addons }
	}

	const getLocationPoint = (location: ILocationModel | null) => {
		if (!location) return ''
		// @ts-ignore no model key
		const { city, address_street } = location

		const sanitize = (value: string): string =>
			value
				.replace(/^ul\.\s*/i, '') // przedrostek "ul."
				.replace(/[\s\-–—]+/g, '_')
				.toLowerCase()
		const sanitizedCity = city ? sanitize(city) : ''
		const sanitizedStreet = address_street ? sanitize(address_street) : ''
		return [sanitizedCity, sanitizedStreet].filter(Boolean).join('_')
	}

	return {
		searchButtonClicked,
		searchListingViewed,
		listingFiltersInteraction,
		vehicleClicked,
		vehicleViewed,
		bookingStarted,
		bookingServiceAdded,
		checkoutStarted,
		addPaymentInfo,
		insuranceSelected,
		pushParamsToDataLayer,
		formatDate,
		getLocationPoint,
		userLogin,
		userRegister,
		formStarted,
		newsletterSubmit,
		pageViewed,
		offerInterest,
		purchase,
	}
}
